'use client';

import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { useEffect } from 'react';

import { fetchDatadogUserData } from '../../actions/fetchUserDataAction';

interface DatadogRumInitProps {
    config: Omit<RumInitConfiguration, 'defaultPrivacyLevel'> & { defaultPrivacyLevel: string };
    enabled?: string | undefined;
    /**
     * When true, only logged-in members record session replay; anonymous visitors are sampled at 0.
     * This requires resolving the user before init so replay sampling can key off the segment, which
     * adds one user-data round trip before RUM starts. Off by default: both segments record at the
     * configured rate, so unsetting the flag restores full-parity sampling with no code change.
     */
    membersOnlyReplay?: boolean;
}

export default function DatadogRumInit({ config, enabled, membersOnlyReplay = false }: DatadogRumInitProps): null {
    useEffect(() => {
        if (enabled !== 'true') return;
        if (datadogRum.getInitConfiguration()) return; // Already initialized by another instance

        const applyUser = (userData: Awaited<ReturnType<typeof fetchDatadogUserData>> | null): void => {
            if (userData) {
                datadogRum.setUser({
                    id: userData.userId?.toString(),
                    isLoggedIn: userData.isLoggedIn,
                });
            }
        };

        if (!membersOnlyReplay) {
            datadogRum.init(config as RumInitConfiguration);
            void fetchDatadogUserData().then(applyUser);
            return;
        }

        // Members-only: resolve the segment first, then init so visitors record no replay.
        const initWithReplayRate = (sessionReplaySampleRate: number): void => {
            if (datadogRum.getInitConfiguration()) return; // guard again after the await
            datadogRum.init({ ...(config as RumInitConfiguration), sessionReplaySampleRate });
        };

        void fetchDatadogUserData()
            .then((userData) => {
                initWithReplayRate(userData?.isLoggedIn ? config.sessionReplaySampleRate ?? 0 : 0);
                applyUser(userData);
            })
            .catch(() => {
                // Segment resolution failed (e.g. the server action's transport rejected). Still
                // start RUM with replay off so the session keeps error and resource telemetry.
                initWithReplayRate(0);
            });
    }, [config, enabled, membersOnlyReplay]);

    return null;
}
